<script>
export class UnsupportedBecauseAllowMultipleViewsToBeExpandedAtOnceIsEnabled extends Error {
    /**
     * @param {string} methodName
     */
    constructor(methodName) {
        super('`' + methodName + '` only works if `allowMultipleViewsToBeExpandedAtOnce` is disabled');
    }
}

export class UnsupportedBecauseAllowMultipleViewsToBeExpandedAtOnceIsDisabled extends Error {
    /**
     * @param {string} methodName
     */
    constructor(methodName) {
        super('`' + methodName + '` only works if `allowMultipleViewsToBeExpandedAtOnce` is enabled');
    }
}

export default {
    props: {
        items: {
            required: false,
            default: () => {
                return [];
            },
            type: Array,
        },

        /**
         * If the user should be able to click, for example, the header to open that view.
         */
        allowUserNavigation: {
            required: false,
            default: true,
            type: Boolean,
        },

        /**
         * If multiple views should be able to be expanded at once.
         */
        allowMultipleViewsToBeExpandedAtOnce: {
            required: false,
            default: false,
            type: Boolean,
        },

        showViewIndex: {
            required: false,
            default: true,
            type: Boolean,
        },

        /**
         * The size of the component.
         * Affects things like padding, margin, font-size, and more.
         */
        size: {
            default: 'md',
            type: String,
            validator: value => ['xs', 'sm', 'md'].includes(value),
        },

        /**
         *
         */
        mountAllAccordionItems: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    data() {
        return {
            internalItems: this.items,

            /**
             * The index of the active view.
             *
             * -1 means that no view is visible and `allowMultipleViewsToBeExpandedAtOnce` is set to false. -2 means
             * that `allowMultipleViewsToBeExpandedAtOnce` is enabled and this variable is a NOP. Do not that we
             * don't have any logic that checks if the value is -2. The -2 is only written to this variable to make
             * it easier when debugging, so you aren't confused with the -1 value that has a different meaning.
             *
             * @type {Number}
             */
            activeViewIndex: -1,
        };
    },
    computed: {
        sortedInternalItems() {
            return this.internalItems.sort((a, b) => a.sortIndex - b.sortIndex);
        },
    },
    created() {
        this.internalItems = this.getEnabledInternalItems();
    },
    mounted() {
        this.setVisibilityAtIndex(0, true);
    },
    methods: {
        showCheckmark(itemSortIndex) {
            if (this.allowMultipleViewsToBeExpandedAtOnce) {
                return false;
            }

            if (this.activeViewIndex < 0) {
                return false;
            }

            if (itemSortIndex < this.internalItems[this.activeViewIndex].sortIndex) {
                return true;
            }

            return false;
        },

        /**
         * Returns the id value of the active view, and null if no view is visible.
         *
         * NOTE: Only works if `allowUserNavigation` is disabled.
         *
         * @returns {Number|null}
         */
        getActiveViewId() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnce('getActiveViewId');

            if (this.activeViewIndex < 0) {
                return null;
            }

            return this.internalItems[this.activeViewIndex].id;
        },

        /**
         * Returns an array of active view ids, and an empty array if no views are visible.
         *
         * NOTE: Only works if `allowUserNavigation` is enabled.
         *
         * @returns {Number[]}
         */
        getActiveViewIds() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnceIsDisabled('getActiveViewIds');

            return this.internalItems
                .filter(i => i.visible === true)
                .map(i => i.id);
        },

        /**
         * Returns the component instance of the active view.
         *
         * NOTE: Only works if `allowUserNavigation` is disabled.
         */
        getComponentInstance() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnce('getComponentInstance');

            return this.$refs.component[0];
        },

        /**
         * Returns an array of component instances from the active views.
         *
         * NOTE: Only works if `allowUserNavigation` is enabled.
         */
        getComponentInstances() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnceIsDisabled('getComponentInstances');

            return this.$refs.component;
        },

        onHeaderClick(index) {
            if (!this.allowUserNavigation) {
                return;
            }

            const currentValue = this.internalItems[index].visible || false;
            this.setVisibilityAtIndex(index, !currentValue);
        },

        /**
         * Check if we are at the last view.
         *
         * NOTE: Only works if `allowUserNavigation` is disabled.
         */
        isLastView() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnce('isLastView');

            // If we have zero views expanded, then we are not on the first view.
            if (this.activeViewIndex < 0) {
                return false;
            }

            return this.sortedInternalItems[this.sortedInternalItems.length - 1].visible === true;
        },

        /**
         * Move to the next view.
         * If you are on the last view and call this, then this method does nothing.
         *
         * NOTE: Only works if `allowUserNavigation` is disabled.
         */
        moveToNextView() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnce('moveToNextView');

            if (this.isLastView()) {
                return;
            }

            ++this.activeViewIndex;
            this.setVisibilityAtIndex(this.activeViewIndex, true);
        },

        /**
         * Returns true if the first view is visible.
         *
         * NOTE: Only works if `allowUserNavigation` is disabled.
         *
         * @returns {boolean}
         */
        isFirstView() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnce('isFirstView');

            // If we have zero views expanded, then we are not on the first view.
            if (this.activeViewIndex < 0) {
                return false;
            }

            return this.sortedInternalItems[0].visible === true;
        },

        /**
         * Move to the previous view in the accordion.
         *
         * NOTE: Only works if `allowUserNavigation` is disabled.
         */
        moveToPreviousView() {
            this._throwIfAllowMultipleViewsToBeExpandedAtOnce('moveToPreviousView');

            // We don't have any expanded views, so we can't do anything.
            if (this.activeViewIndex < 0) {
                return;
            }

            // NOTE: It would maybe be useful not to return early here and allow `moveToPreviousView` to be called
            //       when the first view is expanded. The result of doing that would be closing the first view as
            //       well, so we would have zero expanded rows. I think this only makes sense if
            //       `allowMultipleViewsToBeExpandedAtOnce` is true! I don't have a use for this addition right now,
            //       so I am just leaving a note here for anyone who ends up here.
            if (this.isFirstView()) {
                return;
            }

            --this.activeViewIndex;
            this.setVisibilityAtIndex(this.activeViewIndex, true);
        },

        /**
         * Set the visibility for a specific view.
         * This method will hide all other views.
         */
        setVisibilityAtIndex(index, visible) {
            for (let i = 0; i < this.internalItems.length; i++) {
                if (i === index) {
                    if (!this.allowMultipleViewsToBeExpandedAtOnce) {
                        this.activeViewIndex = i;
                    }
                    this.internalItems[i].visible = visible;
                } else {
                    if (!this.allowMultipleViewsToBeExpandedAtOnce) {
                        this.internalItems[i].visible = false;
                    }
                }
            }

            if (this.allowMultipleViewsToBeExpandedAtOnce) {
                this.activeViewIndex = -2;
            } else if (this._isAllViewsHidden()) {
                this.activeViewIndex = -1;
            }
        },

        getEnabledInternalItems() {
            return this.internalItems.filter((item) => !item.disabled);
        },

        /**
         * Returns true if all views are hidden, otherwise false.
         *
         * @returns {boolean}
         * @private
         */
        _isAllViewsHidden() {
            // NOTE: We could be a lot smarter here to avoid the loop here. One example is keeping a count that we
            //       increment/decrement each time we change a view's `visible` state. I don't want to make this
            //       more complicated than it has to be before we can notice this performance issue, and thus, I
            //       decided that looping through each item would be a simple first step in making this work.

            for (const internalItem of this.internalItems) {
                if (internalItem.visible) {
                    return false;
                }
            }

            return true;
        },

        /**
         * Some public APIs only make sense when `allowMultipleViewsToBeExpandedAtOnce` is disabled. This method
         * ensures we throw an error if such API is called when the multi-view feature is enabled.
         *
         * Do note that some public APIs that we currently have disabled could be made functional but were
         * disabled for simplicity because we did not have a use case for those APIs when this was added.
         *
         * @param {string} methodName
         *     The name of the method that was called (only used to make the error message easier to understand).
         *
         * @private
         */
        _throwIfAllowMultipleViewsToBeExpandedAtOnce(methodName) {
            if (this.allowMultipleViewsToBeExpandedAtOnce) {
                throw new UnsupportedBecauseAllowMultipleViewsToBeExpandedAtOnceIsEnabled(methodName);
            }
        },

        /**
         * See {@link _throwIfAllowMultipleViewsToBeExpandedAtOnce} for a more detailed explanation. This is just
         * a slightly different version of the same check.
         *
         * @param {string} methodName
         *     The name of the method that was called (only used to make the error message easier to understand).
         *
         * @private
         */
        _throwIfAllowMultipleViewsToBeExpandedAtOnceIsDisabled(methodName) {
            if (!this.allowMultipleViewsToBeExpandedAtOnce) {
                throw new UnsupportedBecauseAllowMultipleViewsToBeExpandedAtOnceIsDisabled(methodName);
            }
        },
    },
    compatConfig: {
        INSTANCE_LISTENERS: false,
    },
};
</script>

<template>
    <div class="accordion" :class="[allowUserNavigation ? 'navigation' : '', 'accordion-size-' + size]">
        <div
            v-for="(item, index) in sortedInternalItems"
            class="item"
        >
            <div
                class="header flex"
                :class="{ open: item.visible }"
                @click="onHeaderClick(index)"
            >
                <div>{{ item.title }}</div>
                <div class="flex-grow"></div>
                <div v-if="showCheckmark(item.sortIndex)"><i class="fa-solid fa-circle-check fa-2xl primary-color"></i></div>
                <div v-if="item.extraInfo" class="ml-5">{{ item.extraInfo }}</div>
                <div v-if="showViewIndex" class="ml-5">({{ index + 1 }}/{{ internalItems.length }})</div>
            </div>
            <template v-if="mountAllAccordionItems">
                <div class="content" v-show="item.visible">
                    <component
                        :is="item.component"
                        :key="item.id"
                        ref="component"
                        v-bind="{ ...$attrs, ...item.props }"
                    />
                </div>
            </template>
            <template v-else>
                <div class="content" v-if="item.visible">
                    <component
                        :is="item.component"
                        :key="item.id"
                        ref="component"
                        v-bind="{ ...$attrs, ...item.props }"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
@import 'Sass/system/global/colors';

.primary-color {
    color: $primary-color
}

.accordion {
    border: 1px solid $border-color;

    > .item {
        &:not(:last-child) {
            border-bottom: 1px solid $border-color;
        }

        .header {
            padding: 10px;
            font-weight: bold;
            font-size: 14px;

            &.open {
                color: #fff;
                background-color: $primary-color;
                border-bottom: 1px solid $border-color;
            }
        }

        .content {
            padding: 10px;
        }
    }

    &.navigation {
        > .item {
            .header {
                &:hover {
                    cursor: pointer;
                }
            }
        }
    }
}

.accordion.accordion-size-sm {
    > .item {
        .header {
            padding: 15px;
            font-size: 16px;
        }

        .content {
            padding: 15px;
        }
    }
}

.accordion.accordion-size-md {
    > .item {
        .header {
            padding: 20px;
            font-size: 16px;
        }

        .content {
            padding: 20px;
        }
    }
}
</style>
